import type { McpMenuItemConfig } from './mcpMenuItemsConfig';
import type { Operation } from 'oas/operation';
import type { PropsWithChildren, FC } from 'react';

import copy from 'clipboard-copy';
import { useEffect, useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '@core/context';
import useAskAIState from '@core/hooks/useAskAIState';
import useClassy from '@core/hooks/useClassy';
import { useProjectStore, useSuperHubStore } from '@core/store';
import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Icon from '@ui/Icon';
import Menu, { MenuDivider, MenuHeader, MenuItem } from '@ui/Menu';
import Toast, { showToast } from '@ui/Toast';

import styles from './index.module.scss';
import { mcpMenuItemsConfig } from './mcpMenuItemsConfig';
import { useMarkdownUrl, useMarkdownUrlPrompt } from './useMarkdownUrl';

interface AIDropdownProps {
  className?: string;
  doc?: { deprecated?: boolean; excerpt?: string; slug: string; title: string; type?: string };
  // These can now be optional as the component can fetch them itself
  enableAskAI?: boolean;
  enableChatGPT?: boolean;
  enableClaude?: boolean;
  enableClipboard?: boolean;
  enableViewAsMarkdown?: boolean;
  isPreview?: boolean;
  // New props for OAS enhancement
  operation?: Operation;
  // Add new props to control visibility
  showAIDropdown?: boolean;
}

/**
 * AIDropdown component for sharing the current page URL with AI tools like ChatGPT and Claude
 * This dropdown provides options to open the current page in various AI assistants
 * or copy the URL to the clipboard.
 *
 * The component can now determine its own visibility and fetch AI options from the project store.
 */
const AIDropdown: FC<PropsWithChildren<AIDropdownProps>> = ({
  className,
  // We'll use these props as fallbacks if provided
  enableAskAI: enableAskAIProp,
  enableChatGPT: enableChatGPTProp,
  enableClaude: enableClaudeProp,
  enableClipboard: enableClipboardProp,
  enableViewAsMarkdown: enableViewAsMarkdownProp,
  isPreview = false,
  // New props for visibility control
  showAIDropdown = false,
  // New props for OAS enhancement
  doc,
}) => {
  const { t } = useTranslation();
  // Get the AI options from the project store
  const [
    storeEnableAskAI,
    storeEnableChatGPT,
    storeEnableClaude,
    storeEnableClipboard,
    storeEnableViewAsMarkdown,
    aiDropdownSettingEnabled,
    privacyView,
    mcpEnabled,
    storeMcpCursor,
    storeMcpVscode,
    storeMcpConfig,
    storeMcpCommand,
  ] = useProjectStore(s => [
    s.data.appearance.ai?.options?.ask_ai === 'enabled',
    s.data.appearance.ai?.options?.chatgpt === 'enabled',
    s.data.appearance.ai?.options?.claude === 'enabled',
    s.data.appearance.ai?.options?.clipboard === 'enabled',
    s.data.appearance.ai?.options?.view_as_markdown === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
    s.data.privacy?.view,
    s.data.mcp?.state === 'enabled',
    s.data.appearance.ai?.options?.mcp?.cursor === 'enabled',
    s.data.appearance.ai?.options?.mcp?.vscode === 'enabled',
    s.data.appearance.ai?.options?.mcp?.config === 'enabled',
    s.data.appearance.ai?.options?.mcp?.command === 'enabled',
  ]);

  // AI dropdown is only available for public projects
  const aiDropdownEnabledFromStore = aiDropdownSettingEnabled && privacyView === 'public';
  const [isSuperHub, toggleOwlbot] = useSuperHubStore(s => [s.isSuperHub, s.toggleOwlbot]);

  const markdownUrl = useMarkdownUrl();
  const getMarkdownUrlPrompt = useMarkdownUrlPrompt();

  // Use props if provided, otherwise use store values
  const enableAskAI = enableAskAIProp !== undefined ? enableAskAIProp : storeEnableAskAI;
  const enableChatGPT = enableChatGPTProp !== undefined ? enableChatGPTProp : storeEnableChatGPT;
  const enableClaude = enableClaudeProp !== undefined ? enableClaudeProp : storeEnableClaude;
  const enableClipboard = enableClipboardProp !== undefined ? enableClipboardProp : storeEnableClipboard;
  const enableViewAsMarkdown =
    enableViewAsMarkdownProp !== undefined ? enableViewAsMarkdownProp : storeEnableViewAsMarkdown;

  const { project } = useContext(ProjectContext);

  const customDomain = project.custom_domain || project.parent?.custom_domain;
  const baseUrl = customDomain ? `https://${customDomain}` : `https://${project.subdomain}.readme.io`;
  const mcpUrl = `${baseUrl}/mcp`;

  const { renderMode } = useAskAIState();
  // Check if Ask AI can actually be used (enabled in settings and the project has AI turned on).
  const canUseAskAI = enableAskAI && renderMode === 'askai';

  /**
   * Fetches clean and enhanced markdown content from the same route as the
   * `View as Markdown` link. This ensures the MD content is consistent with
   * each other and is sanitized.
   */
  const getEnhancedMarkdownContent = useCallback(async () => {
    let result = doc?.title || '';

    const markdownContent = await fetch(markdownUrl)
      .then(res => res.text())
      .catch(() => 'Something went wrong getting markdown content.');

    if (markdownContent) {
      result = `${result}\n\n${markdownContent}`;
    }

    return result;
  }, [doc?.title, markdownUrl]);

  // Determine if we should show the AI dropdown
  // For SuperHub mode, only rely on the prop passed down
  // For non-SuperHub mode, check both the prop and the store setting
  // NOTE: Doc/Body.tsx and API/Header duplicate this guard to skip mounting the lazy chunk.
  // If you update this condition, also update `shouldRenderAIDropdown` in Doc/Body.tsx.
  const shouldShowAIDropdown = isSuperHub
    ? showAIDropdown // SuperHub: only use the prop
    : showAIDropdown || aiDropdownEnabledFromStore; // Non-SuperHub: use prop or store

  // State to track screen width
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1250);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const bem = useClassy(styles, 'AIDropdown');

  const [copyState, setCopyState] = useState<'copied' | 'idle' | 'loading'>('idle');

  const showSuccessToast = (message: string) => {
    showToast(<Toast kind="success" title={message} />, { duration: 3000, position: 'bottom-right' });
  };

  const showErrorToast = (message: string) => {
    showToast(<Toast kind="error" title={message} />, { duration: 3000, position: 'bottom-right' });
  };

  const handleOpenLink = () => {};

  const handleCopyToClipboard = async () => {
    if (isPreview || copyState === 'loading') return;

    setCopyState('loading');

    try {
      const enhancedMarkdownContent = await getEnhancedMarkdownContent();

      if (!enhancedMarkdownContent) {
        showErrorToast(t('ai.noMdToCopy'));
        setCopyState('idle');
        return;
      }

      await copy(enhancedMarkdownContent);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      showErrorToast(t('copyToClipboard.failed'));
      setCopyState('idle');
    }
  };

  const handleViewAsMarkdown = () => {
    if (isPreview) return;

    try {
      // Open the markdown version in a new tab, without showing any toast
      window.open(markdownUrl, '_blank');
    } catch (error) {
      // Only show an error toast if something goes wrong
      showErrorToast(t('ai.mdOpenFailed'));
    }
  };

  const handleAskAI = () => {
    if (isPreview) return;

    try {
      // Toggle the Owlbot chat panel in the SuperHub store
      toggleOwlbot();
    } catch (error) {
      showErrorToast(t('ai.aiOpenFailed'));
    }
  };

  /**
   * Attempts to open a deep link via a protocol handler (e.g., cursor://, vscode:).
   * Detects whether the target app launched using blur/visibilitychange events.
   * If the app doesn't open within 2 seconds, shows a warning with a download link.
   */
  const tryProtocolHandler = (deepLink: string, fallback: { appName: string; downloadUrl: string }) => {
    let didNavigate = false;

    let onBlur: () => void;
    let onVisibilityChange: () => void;

    const cleanup = () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    onBlur = () => {
      didNavigate = true;
      cleanup();
    };

    onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        didNavigate = true;
        cleanup();
      }
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);

    window.location.href = deepLink;

    setTimeout(() => {
      cleanup();
      if (!didNavigate) {
        showToast(
          <Toast dismissible kind="warning">
            <span>
              {t('ai.mcp.appNotFound', { app: fallback.appName })}{' '}
              <a href={fallback.downloadUrl} rel="noopener noreferrer" target="_blank">
                {t('ai.mcp.downloadApp', { app: fallback.appName })}
              </a>
            </span>
          </Toast>,
          { duration: 10000, position: 'bottom-right' },
        );
      }
    }, 2000);
  };

  const handleMcpCursor = () => {
    if (isPreview) return;
    const config = btoa(JSON.stringify({ type: 'http', url: mcpUrl }));
    const deepLink = `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(project.subdomain)}&config=${config}`;
    tryProtocolHandler(deepLink, { appName: 'Cursor', downloadUrl: 'https://www.cursor.com/' });
  };

  const handleMcpVscode = () => {
    if (isPreview) return;
    const config = encodeURIComponent(JSON.stringify({ name: project.subdomain, type: 'http', url: mcpUrl }));
    const deepLink = `vscode:mcp/install?${config}`;
    tryProtocolHandler(deepLink, { appName: 'VS Code', downloadUrl: 'https://code.visualstudio.com/' });
  };

  const handleMcpConfig = async () => {
    if (isPreview) return;
    const config = JSON.stringify({ mcpServers: { [project.subdomain]: { type: 'http', url: mcpUrl } } }, null, 2);
    try {
      await copy(config);
      showSuccessToast(t('ai.mcp.copied'));
    } catch {
      showErrorToast(t('copyToClipboard.failed'));
    }
  };

  const handleMcpCommand = async () => {
    if (isPreview) return;
    const command = `npx add-mcp "${mcpUrl}"`;
    try {
      await copy(command);
      showSuccessToast(t('ai.mcp.copied'));
    } catch {
      showErrorToast(t('copyToClipboard.failed'));
    }
  };

  // Ask AI is always first in the menu when available
  const showAskAI = canUseAskAI && enableAskAI;

  // Structure for AI options (ChatGPT, Claude)
  const aiOptions = [
    {
      id: 'chatgpt',
      enabled: enableChatGPT,
      handler: handleOpenLink,
      icon: 'openai',
      label: 'Open in ChatGPT',
      href: `https://chat.openai.com/?q=${getMarkdownUrlPrompt('ChatGPT')}`,
    },
    {
      id: 'claude',
      enabled: enableClaude,
      handler: handleOpenLink,
      icon: 'claude',
      label: 'Open in Claude',
      href: `https://claude.ai/new?q=${getMarkdownUrlPrompt('Claude')}`,
    },
  ];

  const utilityOptions = [
    {
      id: 'clipboard',
      enabled: enableClipboard,
      handler: handleCopyToClipboard,
      icon: 'copy',
      label: t('ai.mdCopy'),
    },
    {
      id: 'markdown',
      enabled: enableViewAsMarkdown,
      handler: handleViewAsMarkdown,
      icon: 'markdown',
      label: t('ai.mdView'),
    },
  ];

  const mcpHandlerMap: Record<McpMenuItemConfig['optionKey'], () => void> = {
    cursor: handleMcpCursor,
    vscode: handleMcpVscode,
    config: handleMcpConfig,
    command: handleMcpCommand,
  };
  const mcpEnabledMap: Record<McpMenuItemConfig['optionKey'], boolean> = {
    cursor: storeMcpCursor,
    vscode: storeMcpVscode,
    config: storeMcpConfig,
    command: storeMcpCommand,
  };

  const mcpOptions = mcpEnabled
    ? mcpMenuItemsConfig.map(item => ({
        id: item.id,
        enabled: mcpEnabledMap[item.optionKey],
        handler: mcpHandlerMap[item.optionKey],
        icon: item.icon,
        label: t(item.labelKey) as string,
      }))
    : [];

  // Check if any option is enabled and if the dropdown should be shown
  const anyMcpOptionEnabled = mcpOptions.some(opt => opt.enabled);
  const hasEnabledOptions =
    enableChatGPT || enableClaude || enableClipboard || enableViewAsMarkdown || showAskAI || anyMcpOptionEnabled;
  // Don't render the dropdown if no options are enabled or if it shouldn't be shown
  if (!hasEnabledOptions || !shouldShowAIDropdown) {
    return null;
  }

  // Enhanced style to ensure visibility - force inlined styles
  const dropdownStyle = {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    marginBottom: '1rem',
    zIndex: 50,
  };

  const enabledAiOptions = aiOptions.filter(opt => opt.enabled);
  // Exclude "Copy Page" from menu since it's the primary button action
  const enabledUtilityOptions = utilityOptions.filter(opt => opt.enabled && opt.id !== 'clipboard');
  const enabledMcpOptions = mcpOptions.filter(opt => opt.enabled);

  const hasMenuItems = showAskAI || enabledAiOptions.length || enabledUtilityOptions.length || enabledMcpOptions.length;
  const hasItemsBelowAskAI = enabledAiOptions.length || enabledUtilityOptions.length || enabledMcpOptions.length;

  const dropdownItems = (
    <>
      {!!showAskAI && (
        <>
          <MenuItem icon={<Icon aria-hidden="true" name="sparkles" />} onClick={handleAskAI}>
            {t('ai.askAi')}
          </MenuItem>
          {!!hasItemsBelowAskAI && <MenuDivider />}
        </>
      )}

      {enabledAiOptions.map(opt => (
        <MenuItem
          key={opt.id}
          href={opt.href}
          icon={<Icon aria-hidden="true" name={opt.icon} />}
          onClick={opt.handler}
          TagName="a"
          target="_blank"
        >
          {opt.label}
        </MenuItem>
      ))}

      {!!enabledMcpOptions.length && (
        <>
          {!!enabledAiOptions.length && <MenuDivider />}
          <MenuHeader>{t('ai.mcp.header')}</MenuHeader>
          {enabledMcpOptions.map(opt => (
            <MenuItem key={opt.id} icon={<Icon aria-hidden="true" name={opt.icon} />} onClick={opt.handler}>
              {opt.label}
            </MenuItem>
          ))}
        </>
      )}

      {!!enabledUtilityOptions.length && (
        <>
          {!!(enabledAiOptions.length || enabledMcpOptions.length) && <MenuDivider />}
          {enabledUtilityOptions.map(opt => (
            <MenuItem key={opt.id} icon={<Icon aria-hidden="true" name={opt.icon} />} onClick={opt.handler}>
              {opt.label}
            </MenuItem>
          ))}
        </>
      )}
    </>
  );

  return (
    <div className={`${bem('&')} ${className || ''} rm-AIDropdown`} style={dropdownStyle}>
      {hasMenuItems ? (
        <Dropdown align="bottom" appendTo={() => document.body} clickInToClose justify="start" sticky>
          <div style={{ display: 'flex' }}>
            <Button
              kind="secondary"
              onClick={e => {
                e.stopPropagation();
                handleCopyToClipboard();
              }}
              outline
              size="sm"
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' }}
            >
              <Icon aria-hidden="true" name={copyState === 'copied' ? 'check' : 'copy'} />
              {!isSmallScreen && <span>{copyState === 'copied' ? 'Copied' : t('ai.mdCopy')}</span>}
            </Button>
            <Button
              kind="secondary"
              outline
              size="sm"
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                paddingLeft: '8px',
                paddingRight: '8px',
                minWidth: 'auto',
              }}
            >
              <Icon aria-hidden="true" name="chevron-down" />
            </Button>
          </div>
          <Menu>{dropdownItems}</Menu>
        </Dropdown>
      ) : (
        <Button kind="secondary" onClick={handleCopyToClipboard} outline size="sm">
          <Icon aria-hidden="true" name={copyState === 'copied' ? 'check' : 'copy'} />
          {!isSmallScreen && <span>{copyState === 'copied' ? 'Copied' : t('ai.mdCopy')}</span>}
        </Button>
      )}
    </div>
  );
};

export default AIDropdown;
